import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import {
  FOLDER_MAX_DEPTH,
  SCOPE_ROOT_LABEL,
  createFolder,
  folderKeys,
  type TFolder,
  type TFolderScope,
} from '../../entities/folder';

const schema = z.object({
  name: z.string().trim().min(1, 'Name the folder').max(120, 'At most 120 characters'),
});

type TValues = z.infer<typeof schema>;

interface IFolderCreateProps {
  scope: TFolderScope;
  /** The folder the list is showing; the new one goes inside it. */
  parent: TFolder | null;
  onCreated?: (folder: TFolder) => void;
}

export const FolderCreate = ({ scope, parent, onCreated }: IFolderCreateProps) => {
  const [open, setOpen] = useState(false);
  const client = useQueryClient();
  const full = (parent?.depth ?? 0) >= FOLDER_MAX_DEPTH;

  const api = useApiForm<TValues>({
    schema,
    defaultValues: { name: '' },
    onSubmit: async (values) => {
      const folder = await createFolder({
        scope,
        name: values.name,
        parentId: parent?.id ?? null,
      });
      await client.invalidateQueries({ queryKey: folderKeys.list(scope) });
      setOpen(false);
      onCreated?.(folder);
    },
  });

  const show = (next: boolean) => {
    if (next) api.form.reset();
    setOpen(next);
  };

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        disabled={full}
        title={full ? `Folders go at most ${FOLDER_MAX_DEPTH} levels deep` : undefined}
        onClick={() => show(true)}
      >
        New folder
      </Button>

      <Modal
        open={open}
        onOpenChange={show}
        title="New folder"
        description={`In ${parent ? parent.path.map((crumb) => crumb.name).join(' / ') : SCOPE_ROOT_LABEL[scope]}.`}
        submit={{
          label: 'Create folder',
          onSubmit: () => void api.submit(),
          waiting: api.form.formState.isSubmitting,
          waitingLabel: 'Creating',
        }}
      >
        <ApiForm api={api}>
          <Field<TValues> name="name" label="Name">
            <TextInput placeholder="Billing" autoFocus />
          </Field>
          <button type="submit" className="hidden" aria-hidden />
        </ApiForm>
      </Modal>
    </>
  );
};
